import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthFetch } from '../AuthComponent';
import { useStore } from '../StoreContext';

/**
 * useOrdersQuery — maps filter/sort/pagination state to /api/orders calls.
 *
 * Returns:
 *   orders, fetching, error, totalCount, pageNum, hasPrev, hasNext,
 *   goNext, goPrev, refresh, filterOptions
 */
export default function useOrdersQuery({
  statuses,
  clubs,
  teams,
  dateFrom,
  dateTo,
  fulfillByFrom,
  fulfillByTo,
  printeds,
  pickStatuses,
  pressStatuses,
  packStatuses,
  deliveryMethods, deliveryNames,
  pickupStores,
  storeStatuses,
  isCustoms,
  crms,
  sources,
  retailStore,        // Retail Unfulfilled chip filter (Eric, 2026-10-08)
  protectStatuses,
  riskLevels,
  deliveryStatuses,
  locations,
  sku = '',
  skuUnpicked = false,
  skuExclude = false,
  splitTeamFulfillment = false,
  includeArchived = false,
  sortKey = 'created_at',
  sortDir = 'desc',
  pageSize = 50,
  enabled = true,
  emulatingSloc = null,
  urlLoc = null,
}) {
  const storeFetch = useAuthFetch();
  const { activeStore } = useStore();

  const [orders, setOrders]         = useState([]);
  const [fetching, setFetching]     = useState(false);
  const [error, setError]           = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  // Seed pageNum from URL so deep-link / Back-to-Orders preserves the page.
  // Without this the URL-sync effect in OrdersTanStack would strip ?page=N
  // on mount because internal state defaulted to 1.
  const [pageNum, setPageNum]       = useState(() => {
    try {
      const p = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    } catch { return 1; }
  });
  const [filterOptions, setFilterOptions] = useState({ clubs: [], teams_by_club: {}, pickup_stores: [], crms: [] });

  // DB keyset cursor stack for pagination
  const [dbCursor, setDbCursor]       = useState(null);
  const [dbCurCursor, setDbCurCursor] = useState(null);
  const [dbPrevCursors, setDbPrevCursors] = useState([]);

  // Increment on every fetch to ignore stale responses
  const fetchVersionRef = useRef(0);

  // Build URLSearchParams from current filter state
  const buildParams = useCallback((dbCursorOverride, pageJump) => {
    const p = new URLSearchParams();
    (statuses || []).forEach(s => p.append('statuses', s));
    (clubs || []).forEach(c => p.append('clubs', c));
    (teams || []).forEach(t => p.append('teams', t));
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo)   p.set('date_to', dateTo);
    if (fulfillByFrom) p.set('fulfill_by_from', fulfillByFrom);
    if (fulfillByTo)   p.set('fulfill_by_to',   fulfillByTo);
    (printeds || []).forEach(v => p.append('printeds', v));
    (pickStatuses || []).forEach(v => p.append('pick_statuses', v));
    (pressStatuses || []).forEach(v => p.append('press_statuses', v));
    (packStatuses || []).forEach(v => p.append('pack_statuses', v));
    (deliveryMethods || []).forEach(v => p.append('delivery_methods', v));
    (deliveryNames || []).forEach(v => p.append('delivery_names', v));
    (pickupStores || []).forEach(s => p.append('pickup_stores', s));
    (storeStatuses || []).forEach(v => p.append('store_statuses', v));
    (isCustoms || []).forEach(v => p.append('is_customs', v));
    (crms || []).forEach(v => p.append('crms', v));
    (sources || []).forEach(v => p.append('sources', v));
    if (retailStore) p.append('retail_store', '1');
    (protectStatuses || []).forEach(v => p.append('protect_statuses', v));
    (riskLevels || []).forEach(v => p.append('risk_levels', v));
    (deliveryStatuses || []).forEach(v => p.append('delivery_statuses', v));
    (locations || []).forEach(v => p.append('locations', v));
    if ((sku || '').trim()) {
      p.set('sku', sku.trim());
      if (skuUnpicked) p.set('sku_unpicked', '1');
      if (skuExclude) p.set('sku_exclude', '1');
    }
    if (splitTeamFulfillment) p.set('split_team_fulfillment', '1');
    if (includeArchived) p.set('include_archived', '1');
    if (urlLoc) p.set('loc', urlLoc);
    p.set('sort_key', sortKey);
    p.set('sort_dir', sortDir);

    if (pageJump && pageJump > 1) {
      p.set('page', String(pageJump));
    } else if (dbCursorOverride) {
      p.set('db_cursor', dbCursorOverride);
    }

    return p;
  }, [statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds, pickStatuses, pressStatuses,
      packStatuses, deliveryMethods, deliveryNames, pickupStores, storeStatuses, isCustoms, crms,
      sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived, sortKey, sortDir, urlLoc]);

  const fetchPage = useCallback(async (cursorOverride, pageJumpOverride) => {
    fetchVersionRef.current += 1;
    const version = fetchVersionRef.current;
    setFetching(true);
    setError(null);
    try {
      const params = buildParams(cursorOverride, pageJumpOverride);
      const res = await storeFetch(`/api/orders?${params}`);
      if (version !== fetchVersionRef.current) return;
      if (!res.ok) throw new Error('Failed to load orders');
      const data = await res.json();
      if (version !== fetchVersionRef.current) return;
      setOrders(data.orders || []);
      if (data.total != null) setTotalCount(data.total);
      setDbCursor(data.next_cursor ?? null);
    } catch (e) {
      if (version === fetchVersionRef.current) setError(e.message);
    } finally {
      if (version === fetchVersionRef.current) setFetching(false);
    }
  }, [buildParams, storeFetch]);

  const fetchCount = useCallback(async () => {
    try {
      const params = buildParams(null, null);
      const res = await storeFetch(`/api/orders/count?${params}`);
      if (res.ok) { const d = await res.json(); setTotalCount(d.count); }
    } catch {}
  }, [buildParams, storeFetch]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo)   p.set('date_to', dateTo);
      (crms || []).forEach(v => p.append('crms', v));
      const res = await storeFetch(`/api/orders/filter-options?${p}`);
      if (res.ok) {
        const d = await res.json();
        setFilterOptions(prev => ({ ...prev, ...d }));
      }
    } catch {}
  }, [dateFrom, dateTo, crms, storeFetch]);

  // Reset pagination + refetch when any filter/sort changes.
  // Depend on a STABLE serialized filter key, NOT the callback references —
  // parent passes new array refs on every render, which would otherwise make
  // fetchPage/fetchCount/fetchFilterOptions fire on EVERY render (was causing
  // duplicate API calls + an infinite cursor-prefetch loop on initial load).
  const filterKey = JSON.stringify({
    statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds,
    pickStatuses, pressStatuses, packStatuses,
    deliveryMethods, deliveryNames, pickupStores, storeStatuses,
    isCustoms, crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived,
    sortKey, sortDir, activeStore,
    emulatingSloc,
    urlLoc,
  });
  // On first mount, honor the page seeded from the URL by jumping directly to
  // that page (uses backend's pageJump path). On subsequent filter changes,
  // reset to page 1.
  const isFirstMountRef = useRef(true);
  useEffect(() => {
    if (!enabled) return;
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      const initialPage = pageNum;
      if (initialPage > 1) {
        fetchPage(null, initialPage);
      } else {
        fetchPage(null, null);
      }
    } else {
      setPageNum(1);
      setDbCursor(null);
      setDbCurCursor(null);
      setDbPrevCursors([]);
      setTotalCount(null);
      fetchPage(null, null);
    }
    fetchCount();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, enabled]);

  const hasPrev = pageNum > 1 || dbPrevCursors.length > 0;
  // Cap hasNext by totalCount when we know it. Without this, the Next button
  // stayed clickable past the real end of results: cursor pagination alone
  // can leave dbCursor non-null on the boundary page (an anchor row that the
  // next fetch reveals is beyond the filter), and URL-jumped pages beyond
  // the last legitimate page were rendering empty with Next still enabled.
  const _totalPages = (totalCount != null && pageSize > 0)
    ? Math.max(1, Math.ceil(totalCount / pageSize))
    : null;
  const hasNext = dbCursor !== null && (_totalPages == null || pageNum < _totalPages);

  // Overshoot guard: URL-seeded ?page=N where N > totalPages (or user hit
  // Next past a stale end-of-list). Snap back to the last real page and
  // refetch. Runs only after totalCount arrives so we never preempt an
  // in-flight fetch. Mirrors the search-mode overshoot guard in
  // OrdersTanStack.jsx (~line 780).
  useEffect(() => {
    if (!enabled) return;
    if (totalCount == null || pageSize <= 0) return;
    const maxPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (pageNum > maxPage) {
      setPageNum(maxPage);
      setDbCursor(null);
      setDbCurCursor(null);
      setDbPrevCursors([]);
      fetchPage(null, maxPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount, pageSize, pageNum, enabled]);

  const goNext = useCallback(() => {
    if (!dbCursor) return;
    setPageNum(n => n + 1);
    setDbPrevCursors(prev => [...prev, dbCurCursor]);
    setDbCurCursor(dbCursor);
    fetchPage(dbCursor, null);
  }, [dbCursor, dbCurCursor, fetchPage]);

  const goPrev = useCallback(() => {
    if (pageNum <= 1 && dbPrevCursors.length === 0) return;
    setPageNum(n => Math.max(1, n - 1));
    if (dbPrevCursors.length > 0) {
      const prev = dbPrevCursors[dbPrevCursors.length - 1] ?? null;
      setDbPrevCursors(ps => ps.slice(0, -1));
      setDbCurCursor(prev);
      fetchPage(prev, null);
    } else {
      setDbCurCursor(null);
      fetchPage(null, null);
    }
  }, [pageNum, dbPrevCursors, fetchPage]);

  const refresh = useCallback(() => {
    setTotalCount(null);
    fetchPage(dbCurCursor, null);
    fetchCount();
  }, [dbCurCursor, fetchPage, fetchCount]);

  // Also update tags optimistically (for print badge remove)
  const updateOrderTags = useCallback((orderId, tags) => {
    setOrders(prev => prev.map(fo =>
      fo.order?.id === orderId
        ? { ...fo, order: { ...fo.order, tags } }
        : fo
    ));
  }, []);

  return {
    orders,
    fetching,
    error,
    totalCount,
    pageNum,
    hasPrev,
    hasNext,
    goNext,
    goPrev,
    refresh,
    filterOptions,
    updateOrderTags,
    pageSize,
    setPageNum,
  };
}
